import { useQuery } from '@apollo/client';
import {
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ClearIcon from '@material-ui/icons/Clear';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import LaunchRoundedIcon from '@material-ui/icons/LaunchRounded';
import type { ContractTransaction } from 'ethers';
import { ethers } from 'ethers';
import type { FC } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNetwork } from 'wagmi';

import { validEmailRegex } from '../../../../constants/regexs';
import { CoinGeckoContext } from '../../../../contexts/app-state-context/coingecko/coingecko.context';
import { useCloudfrontNftAsset } from '../../../../hooks/cloudfront-hook';
import { useMarketplaceContract } from '../../../../hooks/marketplace-contract.hooks';
import { useGetNft } from '../../../../hooks/nft-api.hook';
import { useStarsContract } from '../../../../hooks/stars-contract.hooks';
import {
  getIsEthListing,
  getIsStarsListing,
} from '../../../../lib/graph/graph-utils';
import type {
  Collection,
  Listing,
} from '../../../../lib/graph/MogulMarketplace';
import { AssetType, ListingType } from '../../../../lib/graph/MogulMarketplace';
import collectionQuery from '../../../../lib/graph/queries/collectionQuery';
import { translateIpfsToPinata } from '../../../../lib/nfts/nft-catalog.service';
import {
  getEthListingTokenPrice,
  getNetworkCurrencySymbol,
  getStarsListingTokenPrice,
  parseEther,
  weiToEth,
} from '../../../../lib/smart-contract-utils';
import { formatFloatField, parseFloatField } from '../../../../utils/form.util';
import { Button } from '../../../core/button';
import { InputField } from '../../../core/input-field';
import { CountDown } from '../../countdown/CountDown.component';
import { Panel } from '../../panel/Panel.component';
import styles from './BidAuctionModal.module.css';
import { WalletETHBalanceCard } from './WalletETHBalanceCard.component';

export interface BidAuctionModalProps {
  listing: Listing;
  isWhitelisted: boolean;
  isWhitelistAddress: boolean;
}
export interface BidAuction extends BidAuctionModalProps {
  hideModal: () => void;
}

export interface BidNFTFormValues {
  placeBidAmount?: number;
}

const BidNft: FC<BidAuction> = ({
  isWhitelisted,
  isWhitelistAddress,
  listing,
  hideModal,
}) => {
  const asset = listing.asset;
  const { starsUsdRate, ethUsdRate } = useContext(CoinGeckoContext);
  const { chain } = useNetwork();

  const dbNft = useGetNft(asset.tokenAddress, asset.tokenId.toString());

  const { cloudfrontUrl: imageCloudfront } = useCloudfrontNftAsset(
    dbNft?.image,
  );
  const { cloudfrontUrl: animationCloudfront } = useCloudfrontNftAsset(
    dbNft?.animation,
  );

  const image = imageCloudfront ?? translateIpfsToPinata(asset.image ?? '');
  const animation_url =
    animationCloudfront ?? translateIpfsToPinata(asset.animation_url ?? '');

  const marketplaceContract = useMarketplaceContract({
    type: listing.asset.type ?? AssetType.Erc1155,
  });
  const starsContract = useStarsContract();

  const [email, setEmail] = useState('');
  const [bidAmount, setBidAmount] = useState<string>(
    weiToEth(listing.highestBidAmount as string),
  );
  const [submitting, setSubmitting] = useState(false);
  const [approvingMarketplace, setApprovingMarketplace] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showEmailField, setEmailFieldVisibility] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [disableEmailField, setDisableEmailField] = useState(true);

  const isEthListing = useMemo(() => getIsEthListing(listing), [listing]);
  const isStarsListing = useMemo(() => getIsStarsListing(listing), [listing]);

  useEffect(() => {
    const setVal = async () => {
      const result = await marketplaceContract.isNotificationsEnabled();
      setNotificationsEnabled(result.email.length > 0);
      marketplaceContract.setNotificationEmail(result.email);
      setDisableEmailField(false);
    };
    void setVal();
  }, []);

  useEffect(() => {
    if (!asset.type && !marketplaceContract.initialized) {
      return;
    }
    void (async () => {
      if (listing.type === ListingType.Listing) {
        const conListing = await marketplaceContract.getListingById(
          listing.listingId as string,
        );

        if (!conListing) {
          return;
        }

        if (isEthListing) {
          const listingPriceEth = getEthListingTokenPrice(
            conListing.listingTokens,
            conListing.listingPrices,
          );
          setBidAmount(listingPriceEth.toString());
        } else {
          const listingPriceStars = getStarsListingTokenPrice(
            conListing.listingTokens,
            conListing.listingPrices,
          );
          setBidAmount(listingPriceStars.toString());
        }
      } else {
        const auction = await marketplaceContract.getAuctionById(
          listing.listingId as string,
        );

        const highestBid = auction?.highestBid.amount.toString();
        const startingPrice = auction?.startingPrice.toString();
        const startingBidPrice =
          highestBid !== undefined && highestBid !== '0'
            ? highestBid
            : startingPrice ?? '0';
        setBidAmount(ethers.utils.formatEther(startingBidPrice));
      }
    })();
  }, [marketplaceContract.initialized, asset]);

  const selection =
    useRef<{
      start: number | null;
      end: number | null;
      element: HTMLInputElement;
    } | null>(null);

  const input = <T extends keyof BidNFTFormValues>(
    field: T,
    parse: (value: string) => string | undefined,
    format: (value: number) => string,
  ) => ({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      const value = target.value;
      const parsed = parse(value);

      selection.current = {
        element: target,
        start: target.selectionStart,
        end: target.selectionEnd,
      };

      if (parsed != null) {
        setBidAmount(parsed);
      }
    },
    value: bidAmount.length > 0 ? format(parseFloat(bidAmount)) : bidAmount,
  });

  const approveMarketplaceStars = async () => {
    setApprovingMarketplace(true);

    let result = false;
    try {
      result = await starsContract.approveInfinite(marketplaceContract.address);
    } finally {
      setApprovingMarketplace(false);
    }

    return result;
  };

  const [tx, setTx] = useState('');
  const [txPending, setTxPending] = useState(false);
  const explorerUrl = `${chain!.blockExplorers!.default.url}/tx/${tx}`;

  const { data: collectionData } = useQuery<{
    collection: Collection;
  }>(collectionQuery, {
    variables: { nftContractAddress: asset.tokenAddress as string },
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();

    if (isWhitelisted && !isWhitelistAddress) {
      toast.error('Wallet address is not eligible for whitelist sale');
      return;
    }

    if (submitting || !marketplaceContract.initialized) {
      return;
    }
    let txn: ContractTransaction | undefined;

    setSubmitting(true);
    try {
      await marketplaceContract
        .placeBid(listing.listingId as string, parseEther(bidAmount))
        .then(result => {
          txn = result;
        })
        .catch(error => {
          setSubmitting(false);
          console.error(error);
        });
      if (txn) {
        setTx(txn.hash);
        setTxPending(true);
        await txn.wait();
        setTxPending(false);
      }
    } catch (error) {
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);
  };

  const currentTime = Math.floor(Date.now() / 1000);
  const startTime = listing.startTime;
  const startDate = new Date(startTime * 1000);
  const endTime = listing.endTime;
  const endDate = new Date(endTime * 1000);
  const numDaysLeft = Math.floor((endTime - currentTime) / (60 * 60 * 24));
  const canPlaceBid =
    bidAmount.length > 0 &&
    parseFloat(bidAmount) >
      parseFloat(weiToEth(listing.highestBidAmount as string));

  useEffect(() => {
    void (async () => {
      const approvedAmount = await starsContract.currentWalletAllowance(
        marketplaceContract.address,
      );
      if (approvedAmount !== '0') {
        setApproved(true);
      }
    })();
  }, [marketplaceContract.initialized, starsContract.initialized]);

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.panel + ' flex flex-col rounded-lg'}
    >
      <div className="flex flex-row">
        {tx === '' ? (
          <div className={styles.sellNFTInfo + ' flex flex-col'}>
            <div className="flex">
              <div
                className={
                  styles.modalSectionLeft + ' flex flex-col items-center pt-6'
                }
              >
                {animation_url ? (
                  <video
                    className={'md:w-96 w-56 m-8'}
                    src={animation_url}
                    autoPlay
                    loop
                    muted
                  />
                ) : (
                  image && (
                    <img
                      src={image}
                      alt="nft"
                      className={'object-contain md:w-96 w-56'}
                    />
                  )
                )}
                <div className={styles.title + ' text-center py-4 mx-6'}>
                  {asset.title}
                </div>
                <p className={styles.sellerEthAddress + ' text-center pb-6'}>
                  Created by{' '}
                  {(asset.creator?.address as string).slice(0, 9) +
                    '...' +
                    (asset.creator?.address as string).slice(-6)}
                </p>
              </div>
              <div className={styles.modalSectionRight + ' flex flex-col pl-4'}>
                <div className={'flex flex-col items-center'}>
                  {startTime <= currentTime && currentTime <= endTime ? (
                    <div
                      className={
                        'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
                        styles.countDown
                      }
                    >
                      <AccessTimeIcon className="ml-2" />
                      <div className="ml-1 font-noto-sans font-bold">
                        {numDaysLeft} day{numDaysLeft !== 1 && 's'}
                      </div>
                      <div className="ml-1 font-noto-sans opacity-20"> | </div>
                      <div className="ml-1">
                        <CountDown
                          date={new Date(endTime * 1000)}
                          numberSize="24px"
                          days={false}
                          fontFamily="NotoSans"
                          displayFirstLetter={true}
                          fontSize={false}
                          delimiter=":"
                        />
                      </div>
                      <Tooltip
                        title={`Timer for when auction will end.`}
                        placement="top-end"
                      >
                        <HelpOutlineIcon className="ml-1 mr-2 opacity-50" />
                      </Tooltip>
                    </div>
                  ) : (
                    <div
                      className={
                        'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
                        styles.countDown
                      }
                    >
                      <AccessTimeIcon className="ml-2" />
                      <div className="ml-1 font-noto-sans font-bold mr-2">
                        {startTime > currentTime
                          ? 'Auction starts at'
                          : 'Auction ended at'}
                        {': '}
                        {startTime > currentTime
                          ? startDate.toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : endDate.toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.smallTitleInfo}>
                  <div
                    className={
                      [styles.title, styles.sellNFTTitleInfo].join(' ') +
                      ' text-center py-4 mx-6'
                    }
                  >
                    {asset.title}
                  </div>
                  <p className={styles.sellerEthAddress + ' text-center'}>
                    Created by{' '}
                    {(asset.creator?.address as string).slice(0, 9) +
                      '...' +
                      (asset.creator?.address as string).slice(-6)}
                  </p>
                </div>

                <div className={[styles.title, styles.sellNFTTitle].join(' ')}>
                  {weiToEth(listing.highestBidAmount as string)}{' '}
                  {isStarsListing ? 'MOGUL' : getNetworkCurrencySymbol()}
                </div>
                <p className={styles.fiatValue}>
                  {isStarsListing
                    ? (
                        parseInt(
                          ethers.utils.formatEther(listing.highestBidAmount),
                          10,
                        ) * starsUsdRate
                      ).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : (
                        parseFloat(
                          ethers.utils.formatEther(listing.highestBidAmount),
                        ) * ethUsdRate
                      ).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                  USD
                </p>

                <form
                  autoComplete="off"
                  onSubmit={handleSubmit}
                  className="mb-6"
                >
                  <p className={styles.inputLabel}>
                    Your Bid{' '}
                    {isStarsListing
                      ? '(MOGUL)'
                      : `(${getNetworkCurrencySymbol()})`}
                  </p>
                  <InputField
                    label=""
                    hideLabel
                    InputProps={{
                      disabled: submitting,
                      className: styles.inputField,
                      ...input(
                        'placeBidAmount',
                        parseFloatField,
                        formatFloatField,
                      ),
                    }}
                  />
                  {notificationsEnabled ? (
                    <p>
                      <Checkbox checked disabled />
                      Already Subscribed to Email Notifications
                    </p>
                  ) : (
                    <>
                      <p>
                        <Tooltip
                          title="To be notified about future marketplace transactions, you will be asked to sign a message on metamask before confirming your current bid. The signed message is only used to verify your wallet address"
                          placement="top-start"
                          arrow
                        >
                          <Checkbox
                            disabled={disableEmailField}
                            onChange={() =>
                              setEmailFieldVisibility(!showEmailField)
                            }
                          />
                        </Tooltip>
                        Subscribe to Email Notifications
                      </p>
                      {showEmailField ? (
                        <InputField
                          label=""
                          className="text-white"
                          InputProps={{
                            onChange: (
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setEmail(e.target.value);
                              marketplaceContract.setNotificationEmail(
                                e.target.value,
                              );
                            },
                          }}
                        />
                      ) : null}
                    </>
                  )}
                  <p className={styles.royalDetails}>
                    The creator of this collection will receive{' '}
                    {Math.floor(
                      (asset.commission?.commissionBasisPoint ??
                        collectionData?.collection.commission
                          ?.commissionBasisPoint ??
                        0) / 100,
                    )}
                    % of the sale total from future sales of this item.
                  </p>
                  <Tooltip
                    disableHoverListener={canPlaceBid}
                    title="Bid too low"
                    placement="top-start"
                    arrow
                  >
                    <span>
                      {approved || !isStarsListing ? (
                        <Button
                          type="submit"
                          fullWidth
                          disabled={
                            !marketplaceContract.initialized ||
                            submitting ||
                            !canPlaceBid ||
                            (showEmailField && !validEmailRegex.test(email)) ||
                            (isWhitelisted ? !isWhitelistAddress : false)
                          }
                          className="flex items-center"
                          style={
                            !marketplaceContract.initialized ||
                            submitting ||
                            !canPlaceBid
                              ? { pointerEvents: 'none' }
                              : {}
                          }
                        >
                          {!submitting ? (
                            'Bid'
                          ) : (
                            <>
                              Placing Bid
                              <CircularProgress
                                className={
                                  'placeholder-white::placeholder ml-4'
                                }
                                size={20}
                              />
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          type="button"
                          style={
                            !marketplaceContract.initialized ||
                            approvingMarketplace ||
                            !canPlaceBid
                              ? { pointerEvents: 'none' }
                              : {}
                          }
                          disabled={
                            !marketplaceContract.initialized ||
                            approvingMarketplace ||
                            !canPlaceBid
                          }
                          onClick={async () => {
                            setApprovingMarketplace(true);
                            const didApprove = await approveMarketplaceStars();
                            setApproved(didApprove);
                            setApprovingMarketplace(false);
                          }}
                        >
                          {!approvingMarketplace ? (
                            'Approve'
                          ) : (
                            <>
                              Approving
                              <CircularProgress
                                className={
                                  'placeholder-white::placeholder ml-4'
                                }
                                size={20}
                              />
                            </>
                          )}
                        </Button>
                      )}
                    </span>
                  </Tooltip>
                </form>
                <div className="mt-auto mb-4">
                  <WalletETHBalanceCard
                    walletBalance={
                      isStarsListing
                        ? marketplaceContract.walletDetails.balance.stars
                            .formatted
                        : marketplaceContract.walletDetails.balance.native
                            .formatted
                    }
                    isStars={isStarsListing}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`${styles.confirmation}`}>
            {txPending ? (
              <div>
                <div className={`${styles.confirmationTitle}`}>
                  Please wait for the transaction to finish.
                </div>
                <div className={`${styles.confirmationProgress}`}>
                  <CircularProgress
                    style={{ color: '#fcd559' }}
                    className={'placeholder-white::placeholder ml-4'}
                    size={40}
                  />
                </div>
                <div>Transaction Hash:</div>
                <a
                  className={`${styles.confirmationLink}`}
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="View on explorer"
                >
                  {tx.substring(0, 8) + '...' + tx.substr(tx.length - 4)}
                  <LaunchRoundedIcon
                    className={`${styles.linkIcon}`}
                    fontSize="small"
                  />
                </a>
              </div>
            ) : (
              <div>
                <div className={`${styles.confirmationTitle}`}>
                  Transaction Completed!
                </div>
                <div>Transaction Hash:</div>
                <a
                  className={`${styles.confirmationLink}`}
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="View on explorer"
                >
                  {tx.substring(0, 8) + '...' + tx.substr(tx.length - 4)}
                  <LaunchRoundedIcon
                    className={`${styles.linkIcon}`}
                    fontSize="small"
                  />
                </a>
              </div>
            )}
          </div>
        )}
        <div className="ml-4 relative">
          <IconButton
            className={'h-10 w-10 top-6 right-4 z-30'}
            style={{
              background: '#000000',
              border: 'none',
              position: 'absolute',
            }}
            onClick={hideModal}
          >
            <ClearIcon style={{ color: 'white' }} />
          </IconButton>
        </div>
      </div>
    </Panel>
  );
};
export const BidAuctionModal =
  (props: BidAuctionModalProps) =>
  ({ hideModal }: BidAuction) => {
    return <BidNft hideModal={hideModal} {...props} />;
  };
